/**
 * Pricing configuration hook
 *
 * Hook for accessing platform pricing configuration with automatic fetching.
 * Returns the pricing config and a markup percentage for use in calculations.
 */

import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { DEFAULT_PRICING_CONFIG } from '@/stores/slices/configSlice';
import { useStore } from '@/stores/store';

import type { PricingConfig } from '@gw/ui/types';

export interface UsePricingConfigResult {
  /** The pricing config (may be default if not loaded) */
  config: PricingConfig;
  /** Platform markup as percentage (e.g., 35 for 35%) */
  platformMarkupPercent: number;
  /** Whether the config is currently loading */
  isLoading: boolean;
  /** Whether the config has been loaded from API */
  hasLoaded: boolean;
  /** Any error that occurred during fetch */
  error: string | null;
}

/**
 * Hook to access platform pricing configuration
 *
 * Automatically fetches the pricing config on mount if not already loaded.
 * Returns default values while loading or on error.
 *
 * @example
 * const { platformMarkupPercent, config } = usePricingConfig();
 * const tokens = calculateSmartTokens(baseCost, platformMarkupPercent);
 */
export function usePricingConfig(): UsePricingConfigResult {
  const { pricingConfig, isLoading, hasLoaded, errors, fetchPricingConfig } = useStore(
    useShallow((state) => ({
      pricingConfig: state.pricingConfig,
      isLoading: state.isLoading?.fetchPricingConfig ?? false,
      hasLoaded: state.hasLoaded?.fetchPricingConfig ?? false,
      errors: state.errors?.fetchPricingConfig ?? null,
      fetchPricingConfig: state.fetchPricingConfig,
    }))
  );

  // Fetch pricing config on mount if not already loaded
  useEffect(() => {
    if (!hasLoaded && !isLoading) {
      fetchPricingConfig();
    }
  }, [hasLoaded, isLoading, fetchPricingConfig]);

  // Use loaded config or default
  const config = pricingConfig ?? DEFAULT_PRICING_CONFIG;

  // Convert rate to percentage (0.35 -> 35)
  const platformMarkupPercent = config.platformMarkupRate * 100;

  return {
    config,
    platformMarkupPercent,
    isLoading,
    hasLoaded,
    error: errors,
  };
}
